import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD = "w-full rounded-2xl bg-white border border-border px-5 py-3.5 text-charcoal placeholder:text-charcoal/40 outline-none transition-all focus:ring-2 focus:ring-coral focus:border-transparent";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", community: "", email: "", phone: "", message: "", website_hp: "" };

function Marq() {
  const items = ["Celebrate Life", "Capture Memories", "Strengthen Community", "Bring Families Together", "Honor Every Resident"];
  return (
    <div className="py-6 bg-plum">
      <Marquee speed={40} gradient={false} className="marquee-fade">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-poppins font-bold text-3xl md:text-4xl text-cream/90">
            {t} <span className="text-marigold">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export default function Contact({ prefillMessage = "" }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [status, setStatus] = useState("");
  const submittingRef = useRef(false);

  useEffect(() => {
    if (prefillMessage) { setForm((f) => ({ ...f, message: prefillMessage })); setDone(false); }
  }, [prefillMessage]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading) return;
    if (!form.name || !form.community || !form.email) {
      setStatus("Please share your name, community, and email so Kay can reach you.");
      toast.error("Please share your name, community, and email so Kay can reach you.");
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      setStatus("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }
    submittingRef.current = true; setLoading(true); setStatus("Sending your message…");
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        ...form,
        referring_page: document.referrer || window.location.pathname,
        utm_source: urlParams.get("utm_source") || "",
        utm_medium: urlParams.get("utm_medium") || "",
        utm_campaign: urlParams.get("utm_campaign") || "",
      };
      await axios.post(`${API}/leads`, payload);
      setDone(true);
      setStatus("Thank you. Kay will personally follow up within one business day.");
      toast.success("Thank you! Kay will personally follow up within one business day.");
      setForm(EMPTY);
    } catch (err) {
      const msg = err.response?.status === 429
        ? "Too many submissions. Please try again shortly."
        : "Something went wrong. Please try again in a moment.";
      setStatus(msg); toast.error(msg);
    } finally { submittingRef.current = false; setLoading(false); }
  };

  return (
    <>
      <Marq />
      <section id="contact" className="py-24 md:py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Eyebrow>Contact</Eyebrow>
            <h2 data-testid="final-cta-heading" className="mt-5 font-poppins font-bold text-charcoal text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Let&rsquo;s create something meaningful together.
            </h2>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-charcoal/70 max-w-md">
              Request a complimentary 15-minute conversation. No pressure—just a friendly discussion about
              your community, your residents, and whether this experience could be a good fit.
            </p>
            <div className="mt-8 rounded-3xl bg-sky/25 p-6">
              <p className="font-semibold text-charcoal">A quick note from Kay</p>
              <p className="mt-2 text-charcoal/70 leading-relaxed">I read every message personally and will follow up within one business day.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="sr-only" role="status" aria-live="polite" data-testid="form-status">{status}</div>
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-success"
                className="rounded-[2rem] bg-white border border-border p-10 md:p-14 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-coral/12 grid place-items-center"><CheckCircle2 size={34} className="text-coral" /></div>
                <h3 className="mt-6 font-poppins font-bold text-2xl md:text-3xl text-charcoal">Thank you.</h3>
                <p className="mt-3 text-lg text-charcoal/70">Kay will personally follow up within one business day.</p>
                <button onClick={() => setDone(false)} data-testid="send-another-btn" className="mt-8 inline-block text-charcoal/60 font-semibold hover:text-charcoal">Send another message</button>
              </motion.div>
            ) : (
              <form data-testid="contact-form" method="post" action={`${API}/leads`} onSubmit={submit} noValidate className="rounded-[2rem] bg-white border border-border p-7 md:p-10">
                {/* Honeypot — hidden from real users AND from the accessibility tree. */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" name="website_hp" type="text" tabIndex={-1} autoComplete="off" value={form.website_hp} onChange={set("website_hp")} />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-semibold text-charcoal mb-2">Name *</label>
                    <input id="contact-name" name="name" required autoComplete="name" data-testid="input-name" className={FIELD} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label htmlFor="contact-community" className="block text-sm font-semibold text-charcoal mb-2">Community or organization *</label>
                    <input id="contact-community" name="community" required autoComplete="organization" data-testid="input-community" className={FIELD} value={form.community} onChange={set("community")} placeholder="Sunny Meadows" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-semibold text-charcoal mb-2">Email *</label>
                    <input id="contact-email" name="email" type="email" required autoComplete="email" inputMode="email" data-testid="input-email" className={FIELD} value={form.email} onChange={set("email")} placeholder="jane@community.com" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-semibold text-charcoal mb-2">Phone <span className="text-charcoal/40 font-normal">(optional)</span></label>
                    <input id="contact-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" data-testid="input-phone" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-charcoal mb-2">Message <span className="text-charcoal/40 font-normal">(optional)</span></label>
                  <textarea id="contact-message" name="message" rows={4} data-testid="input-message" className={FIELD} value={form.message} onChange={set("message")} placeholder="Tell us a little about your community..." />
                </div>
                <button type="submit" data-testid="contact-submit-button" disabled={loading}
                  className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-coral text-cream font-semibold text-lg px-9 py-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-16px_rgba(240,125,106,0.6)] disabled:opacity-60">
                  {loading ? "Sending..." : <>Request a 15-Minute Conversation <Send size={18} /></>}
                </button>
                <p className="mt-3 text-xs text-charcoal/50">By submitting you agree to be contacted about your inquiry. See our privacy note in the footer.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
